// components/PositionsGrid.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./css/PositionsGrid.css";
import { apiFetchJson } from "apiClient";

const REFRESH_MS = 8000;

const fmt2 = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return "-";
    return x.toFixed(2);
};

const fmtMoneyINR = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return "-";
    try {
        return x.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    } catch {
        return x.toFixed(2);
    }
};

const fmtPct = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return "-";
    return `${x.toFixed(2)}%`;
};

const safeUpper = (s) => (s == null ? "" : String(s).toUpperCase());

const SORT_KEYS = {
    SYMBOL: "SYMBOL",
    BUY_VALUE: "BUY_VALUE",
    PNL: "PNL",
    PNL_PCT: "PNL_PCT",
};

const PositionsGrid = () => {
    const [mode, setMode] = useState("NET"); // NET | DAY (default NET)
    const [buckets, setBuckets] = useState({ day: [], net: [] });
    const [activeTab, setActiveTab] = useState("ALL"); // "ALL" or a zerodhaUserId
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState(SORT_KEYS.PNL);
    const [sortDir, setSortDir] = useState("DESC"); // ASC | DESC
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const intervalRef = useRef(null);

    const fetchBuckets = async () => {
        const data = await apiFetchJson("/api/kite-orders/portfolio-level-positions-buckets");
        const day = Array.isArray(data?.day) ? data.day : [];
        const net = Array.isArray(data?.net) ? data.net : [];
        return { day, net };
    };

    const load = async () => {
        try {
            setError("");
            setLoading(true);
            const data = await fetchBuckets();
            setBuckets(data);

            const list = mode === "DAY" ? data.day : data.net;
            const userIds = Array.from(new Set((list || []).map((p) => p.zerodhaUserId).filter(Boolean)));
            if (activeTab !== "ALL" && !userIds.includes(activeTab)) setActiveTab("ALL");
        } catch (e) {
            console.error(e);
            setError("Failed to fetch positions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        intervalRef.current = setInterval(load, REFRESH_MS);
        return () => intervalRef.current && clearInterval(intervalRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const list = mode === "DAY" ? buckets.day : buckets.net;
        const userIds = Array.from(new Set((list || []).map((p) => p.zerodhaUserId).filter(Boolean)));
        if (activeTab !== "ALL" && !userIds.includes(activeTab)) setActiveTab("ALL");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const positions = useMemo(() => (mode === "DAY" ? buckets.day : buckets.net), [buckets, mode]);

    const userTabs = useMemo(() => {
        const ids = Array.from(new Set((positions || []).map((p) => p.zerodhaUserId).filter(Boolean)));
        ids.sort();
        return [ ...ids,"ALL"];
    }, [positions]);

    const rows = useMemo(() => {
        const q = safeUpper(search).trim();

        const list = (positions || [])
            .filter((p) => (activeTab === "ALL" ? true : p.zerodhaUserId === activeTab))
            .filter((p) => (q ? safeUpper(p.tradingsymbol).includes(q) : true))
            .map((p) => {
                const qty = Number(p.quantity) || 0;
                const avg = Number(p.averagePrice) || 0;
                const ltp = Number(p.lastPrice) || 0;
                const pnl = Number(p.pnl) || 0;

                const buyValue = Math.abs(qty) * avg;
                const pnlPct = buyValue !== 0 ? (pnl / buyValue) * 100 : 0;

                return {
                    ...p,
                    _qty: qty,
                    _avg: avg,
                    _ltp: ltp,
                    _pnl: pnl,
                    _buyValue: buyValue,
                    _pnlPct: pnlPct,
                    _symKey: safeUpper(p.tradingsymbol),
                };
            });

        const dir = sortDir === "ASC" ? 1 : -1;

        const cmpNum = (a, b, sel) => (sel(a) - sel(b)) * dir;
        const cmpStr = (a, b, sel) => sel(a).localeCompare(sel(b)) * dir;

        if (sortKey === SORT_KEYS.SYMBOL) {
            list.sort((a, b) => cmpStr(a, b, (x) => x._symKey));
        } else if (sortKey === SORT_KEYS.BUY_VALUE) {
            list.sort((a, b) => cmpNum(a, b, (x) => x._buyValue));
        } else if (sortKey === SORT_KEYS.PNL_PCT) {
            list.sort((a, b) => cmpNum(a, b, (x) => x._pnlPct));
        } else {
            list.sort((a, b) => cmpNum(a, b, (x) => x._pnl));
        }

        return list;
    }, [positions, activeTab, search, sortKey, sortDir]);

    const totals = useMemo(() => {
        let totalPnl = 0;
        let totalBuyValue = 0;
        let totalSellValue = 0;

        for (const r of rows) {
            totalPnl += Number(r._pnl) || 0;

            if (r._qty > 0) {
                totalBuyValue += Number(r._buyValue) || 0;
            } else if (r._qty < 0) {
                totalSellValue += Number(r._buyValue) || 0; // |Qty| × Avg
            }
        }

        const totalPnlPct =
            totalBuyValue !== 0 ? (totalPnl / totalBuyValue) * 100 : 0;

        return {
            totalPnl,
            totalBuyValue,
            totalSellValue,
            totalPnlPct,
            count: rows.length,
        };
    }, [rows]);


    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
        } else {
            setSortKey(key);
            if (key === SORT_KEYS.SYMBOL) setSortDir("ASC");
            else setSortDir("DESC");
        }
    };

    const sortArrow = (key) => {
        if (sortKey !== key) return null;
        return <span className="arrow">{sortDir === "ASC" ? "▲" : "▼"}</span>;
    };

    const GridHeaderCell = ({ className = "", sortKey: sk, title, children }) => {
        const sortable = Boolean(sk);
        const cls = `pg-hcell ${className} ${sortable ? "sort" : ""}`.trim();
        return (
            <div
                className={cls}
                role={sortable ? "button" : undefined}
                title={title}
                onClick={sortable ? () => toggleSort(sk) : undefined}
            >
                {children} {sortable && sortArrow(sk)}
            </div>
        );
    };

    return (
        <div className="pg-wrap">
            <div className="pg-card">
                <div className="pg-tabs-row">
                    <div className="pg-tabs">
                        {userTabs.map((id) => (
                            <button
                                key={id}
                                className={`pg-tab ${activeTab === id ? "active" : ""}`}
                                onClick={() => setActiveTab(id)}
                                title={id === "ALL" ? "All active users" : `User: ${id}`}
                            >
                                {id === "ALL" ? "All" : id}
                            </button>
                        ))}
                    </div>

                    <div className="pg-actions">
                        <div className="pg-toggle" role="group" aria-label="Mode toggle">
                            <button
                                className={`pg-tbtn ${mode === "NET" ? "active" : ""}`}
                                onClick={() => setMode("NET")}
                                title="Net positions (open exposure, required for F&O)"
                            >
                                Net
                            </button>
                            <button
                                className={`pg-tbtn ${mode === "DAY" ? "active" : ""}`}
                                onClick={() => setMode("DAY")}
                                title="Day positions (today's activity)"
                            >
                                Day
                            </button>
                        </div>

                        <div className="pg-search">
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search symbol…"
                                spellCheck={false}
                            />
                            {search && (
                                <button className="pg-x" onClick={() => setSearch("")} title="Clear">
                                    ×
                                </button>
                            )}
                        </div>

                        <button className="pg-btn" onClick={load} disabled={loading} title="Refresh now">
                            {loading ? "Refreshing…" : "Refresh"}
                        </button>
                    </div>
                </div>

                <div className="pg-subline">
                    Refreshes every {REFRESH_MS / 1000}s • Mode: <b>{mode}</b> • Showing <b>{totals.count}</b> position(s) • Default sort:
                    <b> P&amp;L</b> (high → low)
                </div>

                <div className="pg-totals">
                    <div className="t">
                        <div className="k">Total Buy Value</div>
                        <div className="v">{fmtMoneyINR(totals.totalBuyValue)}</div>
                    </div>

                    <div className="t">
                        <div className="k">Total Sell Value</div>
                        <div className="v">{fmtMoneyINR(totals.totalSellValue)}</div>
                    </div>

                    <div className="t">
                        <div className="k">Total P&amp;L</div>
                        <div className={`v ${totals.totalPnl >= 0 ? "pos" : "neg"}`}>
                            {fmtMoneyINR(totals.totalPnl)}
                        </div>
                    </div>

                    <div className="t">
                        <div className="k">Total P&amp;L %</div>
                        <div className={`v ${totals.totalPnlPct >= 0 ? "pos" : "neg"}`}>
                            {fmtPct(totals.totalPnlPct)}
                        </div>
                    </div>
                </div>


                {error && <div className="pg-error">{error}</div>}

                <div className="pg-grid-wrap">
                    {/* Sticky header */}
                    <div className="pg-grid-head">
                        <GridHeaderCell className="left" sortKey={SORT_KEYS.SYMBOL} title="Sort by Symbol">
                            Symbol
                        </GridHeaderCell>
                        <div className="pg-hcell left">Exch</div>
                        <div className="pg-hcell">Qty</div>
                        <div className="pg-hcell">Avg</div>
                        <div className="pg-hcell">LTP</div>
                        <GridHeaderCell sortKey={SORT_KEYS.BUY_VALUE} title="Sort by Buy Value">
                            Buy Value
                        </GridHeaderCell>
                        <GridHeaderCell sortKey={SORT_KEYS.PNL} title="Sort by P&L">
                            P&amp;L
                        </GridHeaderCell>
                        <GridHeaderCell sortKey={SORT_KEYS.PNL_PCT} title="Sort by P&L %">
                            P&amp;L %
                        </GridHeaderCell>
                        <div className="pg-hcell left">Product</div>
                        <div className="pg-hcell left">User</div>
                    </div>

                    {/* Body */}
                    {rows.length === 0 ? (
                        <div className="pg-grid-empty">No positions found.</div>
                    ) : (
                        <div className="pg-grid-body">
                            {rows.map((r) => (
                                <div
                                    className="pg-grid-row"
                                    key={`${r.zerodhaUserId || "u"}-${r.tradingsymbol}-${r.exchange}-${r.product}-${mode}`}
                                >
                                    <div className="pg-cell left sym">{r.tradingsymbol}</div>
                                    <div className="pg-cell left">{r.exchange}</div>
                                    <div className="pg-cell num">{r._qty}</div>
                                    <div className="pg-cell num">{fmt2(r._avg)}</div>
                                    <div className="pg-cell num">{fmt2(r._ltp)}</div>
                                    <div className="pg-cell num">{fmtMoneyINR(r._buyValue)}</div>
                                    <div className={`pg-cell num pnl ${r._pnl >= 0 ? "pos" : "neg"}`}>{fmtMoneyINR(r._pnl)}</div>
                                    <div className={`pg-cell num pnl ${r._pnlPct >= 0 ? "pos" : "neg"}`}>{fmtPct(r._pnlPct)}</div>
                                    <div className="pg-cell left">{r.product}</div>
                                    <div className="pg-cell left user">{r.zerodhaUserId || "-"}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="pg-foot">
                    <div className="hint">
                        Buy Value is computed as <code>|Qty| × Avg</code>. Total P&amp;L% is weighted: <code>Total P&amp;L ÷ Total Buy Value</code>.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PositionsGrid;
